// PHI tokenizer — replaces personal identifiers (name, age, DOB) with [Token]
// placeholders so text can be shared without protected health information.
//
// Extracted from ai_proofread_controller.js for reuse by proofread_bucket.js.

// Build a RegExp that matches `text` as a whole word (case-insensitive).
function wordRx(text) {
  const esc = String(text).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
  return new RegExp(`\\b${esc}\\b`, "gi")
}

// Compute age from a YYYY-MM-DD date string; returns null if unparseable.
function ageFromDob(dob) {
  if (!dob) return null
  const p = String(dob).split("-")
  if (p.length !== 3) return null
  const y = +p[0], m = +p[1] - 1, d = +p[2]
  if (!y || Number.isNaN(m) || !d) return null
  const t = new Date()
  let a = t.getFullYear() - y
  const md = t.getMonth() - m
  if (md < 0 || (md === 0 && t.getDate() < d)) a--
  return a
}

// Build a tokenizer function from plan.client fields. The returned function
// replaces occurrences of firstName, lastName, age (in age-like contexts), and
// DOB with [FirstName] / [LastName] / [Age] / [DOB]. Values already stored as
// [Token] are left alone.
export function buildTokenizer(plan) {
  const c = (plan && plan.client) || {}
  const isToken = (v) => /^\s*\[.*\]\s*$/.test(String(v || ""))
  const first = (c.firstName || "").trim()
  const last  = (c.lastName  || "").trim()
  const age   = ageFromDob(c.dob)
  const dob   = (c.dob || "").trim()
  const reps  = []

  // Full name first ("Sam Smith" → one clean pair), then each part.
  if (first && last && !isToken(first) && !isToken(last)) {
    reps.push([wordRx(`${first} ${last}`), "[FirstName] [LastName]"])
  }
  if (first && !isToken(first)) reps.push([wordRx(first), "[FirstName]"])
  if (last  && !isToken(last))  reps.push([wordRx(last),  "[LastName]"])

  // Age ONLY in age-like contexts — never a bare number, so "3 hours" for a
  // 3-year-old stays "3 hours".  "12 years old" / "12-year-old" / "12 yo".
  if (age != null && age >= 0) {
    reps.push([new RegExp(`\\b${age}(?=[\\s-]*(?:years?|yrs?|yo|year[\\s-]*old)\\b)`, "gi"), "[Age]"])
    reps.push([new RegExp(`\\bage([:\\s]+)${age}\\b`, "gi"), "age$1[Age]"])
  }
  if (dob && !isToken(dob)) reps.push([wordRx(dob), "[DOB]"])

  return (s) => {
    let out = String(s == null ? "" : s)
    for (const [rx, t] of reps) out = out.replace(rx, t)
    return out
  }
}
